// The Fireworks bail style (#199, #210), three.js-free so it can be tested:
// as the rider goes down they become the show. The body pops apart in a
// flash at each part, and a rocket shoots up from where each part was (the
// head, the torso, the legs, the board), whistling, to burst overhead in
// that part's colour (a peony, a crackling willow, a ring), each bang a
// beat after its flash, sparks drifting down and a smoky glow that's gone
// by the respawn. One fixed pool of particles, so a show allocates nothing;
// `bail-fx.ts` hides the rider and draws the pool as one `THREE.Points`.

/** A burst's shape: a ball of stars, drooping crackling strands, or a flat ring. */
export type BurstKind = "peony" | "willow" | "ring";

/** What a show sounds like: a rocket going up, its burst, and the willow's crackle. */
export type FireworkCue = "whistle" | "bang" | "crackle";

/** The parts of the rider that become rockets (#210), in show order. */
export type FireworkPart = "head" | "torso" | "legs" | "board";

/**
 * The show: which part each rocket leaves from, when it goes up (s after the
 * bail), what it bursts into, and how fast (m/s) it fans out sideways, so
 * the bursts open side by side rather than on top of each other.
 */
export const FIREWORK_SHOW: readonly { part: FireworkPart; at: number; kind: BurstKind; fan: number }[] = [
  { part: "head", at: 0, kind: "peony", fan: 0 },
  { part: "torso", at: 0.08, kind: "willow", fan: -2.6 },
  { part: "legs", at: 0.16, kind: "ring", fan: 2.6 },
  { part: "board", at: 0.24, kind: "peony", fan: -0.9 },
];

/** Where a rocket leaves from (the part's spot in the world) and its colour (rgb, 0..1). */
export interface FireworkOrigin {
  x: number;
  y: number;
  z: number;
  color: readonly [number, number, number];
}

/** How long (s) a rocket climbs before it bursts, and how fast it leaves the ground (m/s). */
export const ROCKET_RISE = 0.5;
const ROCKET_SPEED = 10;
const ROCKET_G = 9;
/** The bang comes this long (s) after the flash: sound is slower than light. */
export const BANG_DELAY = 0.15;
/** The willow's crackle, this long after its flash. */
const CRACKLE_DELAY = 0.3;
/** A trail spark every this many seconds of a rocket's climb. */
const TRAIL_EVERY = 0.04;

/** Stars per burst; Low quality (`lite`) halves them. */
const STARS: Record<BurstKind, number> = { peony: 60, willow: 55, ring: 50 };
/** Bits each part pops into as the body comes apart, before its rocket goes. */
const POP_BITS = 8;

/** The pool: every burst, trail spark, flash and glow there can be at once. */
export const MAX_SPARKS = 320;

type Kind = "rocket" | "trail" | "star" | "flash" | "smoke";

interface Spark {
  kind: Kind;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  life: number;
  r: number;
  g: number;
  b: number;
  /** World size (m) of the point. */
  size: number;
  /** Speed lost per second (a fraction, as `exp(-drag * dt)`), and gravity. */
  drag: number;
  fall: number;
  /** A willow star twinkles on and off. */
  crackle: boolean;
  /** A rocket: which show entry it is, and how long until the next trail spark. */
  show: number;
  trail: number;
}

const blank = (): Spark => ({ kind: "star", x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 0, life: 1, r: 1, g: 1, b: 1, size: 0.1, drag: 0, fall: 0, crackle: false, show: 0, trail: 0 });

/** A small deterministic random stream, so every show plays the same. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A colour lifted toward white, so a dark shirt still reads as a firework against the sky. */
export function brighten([r, g, b]: readonly [number, number, number]): [number, number, number] {
  const lift = 0.35;
  return [r + (1 - r) * lift, g + (1 - g) * lift, b + (1 - b) * lift];
}

export class Fireworks {
  /** What's alive, packed from 0 to `count`: xyz per spark, rgb per spark, size (m) and opacity per spark. */
  readonly positions = new Float32Array(MAX_SPARKS * 3);
  readonly colors = new Float32Array(MAX_SPARKS * 3);
  readonly sizes = new Float32Array(MAX_SPARKS);
  readonly alphas = new Float32Array(MAX_SPARKS);
  count = 0;
  /** Low quality: half the stars and trail. */
  lite = false;
  private readonly pool: Spark[] = Array.from({ length: MAX_SPARKS }, blank);
  private t = 0;
  private launched = 0;
  private palette: [number, number, number][] = [[1, 1, 1]];
  /** Each rocket's launch spot and colour, one per `FIREWORK_SHOW` entry. */
  private readonly origins: { x: number; y: number; z: number; color: [number, number, number] }[] = FIREWORK_SHOW.map(() => ({ x: 0, y: 0, z: 0, color: [1, 1, 1] }));
  /** Sideways (world x, z) for the fan: across the camera's view. */
  private rx = 1;
  private rz = 0;
  private rand = rng(199);
  /** Sounds still to come: when, and which. */
  private readonly sounds: { at: number; cue: FireworkCue }[] = [];
  private readonly heard: FireworkCue[] = [];

  /**
   * Starts a show as the rider goes down (#210): `parts` is where each of
   * `FIREWORK_SHOW`'s parts was and its colour (a missing one leaves from
   * the first), `colors` the rider's other colours (rgb, 0..1) mixed into
   * the bursts, and `right` the sideways direction (x, z) the rockets fan
   * along. Each part pops apart in a flash where it was.
   */
  start(parts: readonly FireworkOrigin[], colors: readonly (readonly [number, number, number])[], right: readonly [number, number] = [1, 0]): void {
    this.clear();
    this.rand = rng(199);
    this.palette = colors.length ? colors.map(brighten) : [[1, 1, 1]];
    const len = Math.hypot(right[0], right[1]);
    this.rx = len > 1e-6 ? right[0] / len : 1;
    this.rz = len > 1e-6 ? right[1] / len : 0;
    FIREWORK_SHOW.forEach((_, n) => {
      const from = parts[n] ?? parts[0] ?? { x: 0, y: 0, z: 0, color: [1, 1, 1] as const };
      const o = this.origins[n]!;
      o.x = from.x;
      o.y = from.y;
      o.z = from.z;
      o.color = brighten(from.color);
      this.pop(o);
    });
  }

  /** A part comes apart: a quick flash and a few bits in its colour. */
  private pop(o: { x: number; y: number; z: number; color: [number, number, number] }): void {
    this.spawn("flash", o.x, o.y, o.z, 0, 0, 0, 0.15, o.color, 0.9, 0, 0);
    const n = this.lite ? POP_BITS >> 1 : POP_BITS;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + this.rand();
      this.spawn("star", o.x, o.y, o.z, Math.cos(a) * 2, 1 + this.rand() * 2, Math.sin(a) * 2, 0.35, o.color, 0.08, 2, 6);
    }
  }

  /** Ends the show at once. */
  clear(): void {
    this.count = 0;
    this.t = 0;
    this.launched = 0;
    this.sounds.length = 0;
  }

  /** Whether anything's still to come or on screen. */
  get busy(): boolean {
    return this.count > 0 || this.launched < FIREWORK_SHOW.length || this.sounds.length > 0;
  }

  /**
   * Moves the show on by `dt` and returns what it sounds like this step (the
   * same array each call). `floor` is the ground's height under a point:
   * sparks go out when they reach it.
   */
  step(dt: number, floor: (x: number, z: number) => number = () => -Infinity): readonly FireworkCue[] {
    this.heard.length = 0;
    this.t += dt;
    while (this.launched < FIREWORK_SHOW.length && FIREWORK_SHOW[this.launched]!.at <= this.t) this.launch(this.launched++);
    for (let i = this.sounds.length - 1; i >= 0; i--) {
      const s = this.sounds[i]!;
      if (s.at > this.t) continue;
      this.heard.push(s.cue);
      this.sounds.splice(i, 1);
    }
    this.heard.reverse();
    let i = 0;
    while (i < this.count) {
      const p = this.pool[i]!;
      p.age += dt;
      if (p.kind === "rocket" && p.age >= ROCKET_RISE) {
        this.burst(p);
        this.kill(i);
        continue;
      }
      // A rocket's climbing, so the ground can't put it out (one off the board starts at it, #210).
      if (p.age >= p.life || (p.kind !== "smoke" && p.kind !== "flash" && p.kind !== "rocket" && p.y < floor(p.x, p.z))) {
        this.kill(i);
        continue;
      }
      const k = Math.exp(-p.drag * dt);
      p.vx *= k;
      p.vy = p.vy * k - p.fall * dt;
      p.vz *= k;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      if (p.kind === "rocket") {
        p.trail -= dt;
        if (p.trail <= 0) {
          p.trail += this.lite ? TRAIL_EVERY * 2 : TRAIL_EVERY;
          this.spawn("trail", p.x, p.y, p.z, (this.rand() - 0.5) * 0.6, -1 - this.rand(), (this.rand() - 0.5) * 0.6, 0.3, [1, 0.75, 0.4], 0.09, 2, 4);
        }
      }
      i++;
    }
    this.pack();
    return this.heard;
  }

  private launch(n: number): void {
    const s = FIREWORK_SHOW[n]!;
    const o = this.origins[n]!;
    const wob = () => (this.rand() - 0.5) * 0.8;
    const r = this.spawn("rocket", o.x, o.y, o.z, this.rx * s.fan + wob(), ROCKET_SPEED, this.rz * s.fan + wob(), ROCKET_RISE + 1, o.color, 0.16, 0, ROCKET_G);
    if (r) r.show = n;
    this.sounds.push({ at: s.at, cue: "whistle" });
  }

  /** A rocket's burst: the stars, a flash, a smoky glow, and its bang a beat later. */
  private burst(rocket: Spark): void {
    const kind = FIREWORK_SHOW[rocket.show]!.kind;
    const { x, y, z } = rocket;
    const n = this.lite ? STARS[kind] >> 1 : STARS[kind];
    const tilt = this.rand() * Math.PI;
    for (let i = 0; i < n; i++) {
      const col = i % 3 === 2 ? this.palette[(i + rocket.show) % this.palette.length]! : this.origins[rocket.show]!.color;
      let dx: number;
      let dy: number;
      let dz: number;
      if (kind === "ring") {
        // A ring on edge to the camera, tipped a little.
        const a = (i / n) * Math.PI * 2;
        dx = Math.cos(a);
        dy = Math.sin(a) * Math.cos(tilt * 0.3);
        dz = Math.sin(a) * Math.sin(tilt * 0.3);
      } else {
        // Evenly over a sphere (a Fibonacci spiral), so a peony's a round ball.
        const u = 1 - (2 * (i + 0.5)) / n;
        const a = i * 2.39996 + tilt;
        const rr = Math.sqrt(1 - u * u);
        dx = rr * Math.cos(a);
        dy = u;
        dz = rr * Math.sin(a);
      }
      const willow = kind === "willow";
      const speed = (kind === "ring" ? 5.5 : willow ? 4 : 5) * (0.9 + this.rand() * 0.2);
      const life = willow ? 0.8 : kind === "ring" ? 0.55 : 0.65;
      const s = this.spawn("star", x, y, z, dx * speed, dy * speed, dz * speed, life * (0.9 + this.rand() * 0.1), col, willow ? 0.09 : 0.12, willow ? 2.2 : 1.6, willow ? 5 : 3);
      if (s) s.crackle = willow;
    }
    this.spawn("flash", x, y, z, 0, 0, 0, 0.12, [1, 1, 0.92], 2.2, 0, 0);
    const col = this.origins[rocket.show]!.color;
    this.spawn("smoke", x, y, z, 0, 0, 0, kind === "ring" ? 0.6 : 0.75, [col[0] * 0.5 + 0.4, col[1] * 0.5 + 0.4, col[2] * 0.5 + 0.4], 3.2, 0, -0.4);
    this.sounds.push({ at: this.t + BANG_DELAY, cue: "bang" });
    if (kind === "willow") this.sounds.push({ at: this.t + CRACKLE_DELAY, cue: "crackle" });
  }

  private spawn(
    kind: Kind,
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    [r, g, b]: readonly [number, number, number],
    size: number,
    drag: number,
    fall: number,
  ): Spark | null {
    if (this.count >= MAX_SPARKS) return null;
    const p = this.pool[this.count++]!;
    p.kind = kind;
    p.x = x;
    p.y = y;
    p.z = z;
    p.vx = vx;
    p.vy = vy;
    p.vz = vz;
    p.age = 0;
    p.life = life;
    p.r = r;
    p.g = g;
    p.b = b;
    p.size = size;
    p.drag = drag;
    p.fall = fall;
    p.crackle = false;
    p.show = 0;
    p.trail = 0;
    return p;
  }

  /** Drops spark `i`, moving the last one into its place. */
  private kill(i: number): void {
    const last = --this.count;
    if (i === last) return;
    const tmp = this.pool[i]!;
    this.pool[i] = this.pool[last]!;
    this.pool[last] = tmp;
  }

  /** Writes the live sparks into the arrays the renderer reads. */
  private pack(): void {
    for (let i = 0; i < this.count; i++) {
      const p = this.pool[i]!;
      const k = p.age / p.life;
      let a: number;
      if (p.kind === "flash") a = 1 - k;
      else if (p.kind === "smoke") a = 0.35 * Math.sin(Math.PI * Math.min(1, k * 1.6 + 0.1)) * (1 - k);
      else if (p.kind === "rocket") a = 1;
      else a = (1 - k * k) * (p.crackle && this.rand() < 0.3 ? 0.15 : 1);
      this.positions[i * 3] = p.x;
      this.positions[i * 3 + 1] = p.y;
      this.positions[i * 3 + 2] = p.z;
      this.colors[i * 3] = p.r;
      this.colors[i * 3 + 1] = p.g;
      this.colors[i * 3 + 2] = p.b;
      this.sizes[i] = p.kind === "smoke" ? p.size * (0.6 + 0.6 * k) : p.size;
      this.alphas[i] = Math.max(0, a);
    }
  }
}
